import { semanticColors } from '@aztec-realms/design-tokens';
import type {
  DecodePuzzleDefinition,
  DecodeSegmentView,
  QualityTier,
} from '@aztec-realms/shared-types';

import { environmentAssetManifest, generatedRuntimeAssetManifest } from './asset-manifest';
import { decodeBoardLayout, type DecodeBoardLayout as BoardLayout } from './decode-board-layout';
import { bakeDecodeMaterial, decodeCascadeSegments, visibleDecodeEnergy } from './decode-material';
import {
  decodePortAnchorAngles,
  decodeSegmentCenterAngle,
  orientedDecodePort,
} from './decode-channel-geometry';
import {
  channelsForSegment,
  deriveDecodeVisualState,
  type DecodePort,
  type DecodeVisualState,
} from './decode-visual-state';

export type DecodeVisualEventName =
  | 'segment_rotate'
  | 'connection_made'
  | 'dead_end'
  | 'glyph_resonance'
  | 'puzzle_solved'
  | 'rare_reward';

export interface DecodeVisualEvent {
  name: DecodeVisualEventName;
  segmentId?: string;
}

export interface DecodeRendererState {
  rotations: Readonly<Record<string, number>>;
  selectedSegmentId?: string;
  solved: boolean;
  suggestedSegmentId?: string;
}

export interface DecodeRendererOptions {
  qualityTier: QualityTier;
  reducedMotion: boolean;
  onSegmentSelected: (segmentId: string) => void;
  onVisualEvent?: (event: DecodeVisualEvent) => void;
  onReady: () => void;
  onUnavailable?: () => void;
}

export interface DecodeRendererController {
  update: (state: DecodeRendererState) => void;
  resize: () => void;
  destroy: () => void;
}

function color(value: string): number {
  return Number.parseInt(value.slice(1), 16);
}

function shade(value: number, amount: number): number {
  const red = Math.max(0, Math.min(255, ((value >> 16) & 0xff) + amount));
  const green = Math.max(0, Math.min(255, ((value >> 8) & 0xff) + amount));
  const blue = Math.max(0, Math.min(255, (value & 0xff) + amount));
  return (red << 16) | (green << 8) | blue;
}

const palette = {
  deep: color(semanticColors.backgroundDeep),
  panel: color(semanticColors.backgroundPanel),
  elevated: color(semanticColors.backgroundElevated),
  jade: color(semanticColors.jade),
  turquoise: color(semanticColors.turquoise),
  gold: color(semanticColors.sunGold),
  solar: color(semanticColors.solarGold),
  stone: color(semanticColors.sandstone),
};

const material = {
  obsidianDeep: shade(palette.deep, -5),
  obsidianSheen: shade(palette.elevated, 15),
  stoneDark: shade(palette.stone, -62),
  stoneMid: shade(palette.stone, -18),
  stoneLight: shade(palette.stone, 30),
  stoneShade: shade(palette.stone, -38),
};

interface Point {
  x: number;
  y: number;
}

function annularPoints(
  centerX: number,
  centerY: number,
  innerRadius: number,
  outerRadius: number,
  start: number,
  end: number,
): Point[] {
  const points: Point[] = [];
  const steps = 14;
  for (let step = 0; step <= steps; step += 1) {
    const angle = start + ((end - start) * step) / steps;
    points.push({
      x: centerX + Math.cos(angle) * outerRadius,
      y: centerY + Math.sin(angle) * outerRadius,
    });
  }
  for (let step = steps; step >= 0; step -= 1) {
    const angle = start + ((end - start) * step) / steps;
    points.push({
      x: centerX + Math.cos(angle) * innerRadius,
      y: centerY + Math.sin(angle) * innerRadius,
    });
  }
  return points;
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function normalizeRotation(rotation: number): number {
  return ((rotation % 4) + 4) % 4;
}

function pointBetween(left: Point, right: Point, progress: number): Point {
  return {
    x: left.x + (right.x - left.x) * progress,
    y: left.y + (right.y - left.y) * progress,
  };
}

function particleCount(qualityTier: QualityTier): number {
  if (qualityTier === 'low') return 2;
  if (qualityTier === 'high') return 7;
  return 4;
}

export async function mountDecodeRenderer(
  parent: HTMLElement,
  puzzle: DecodePuzzleDefinition,
  initialState: DecodeRendererState,
  options: DecodeRendererOptions,
): Promise<DecodeRendererController> {
  const PhaserModule = await import('phaser');
  const Phaser = PhaserModule.default;
  let state = initialState;
  let destroyed = false;

  const emit = (event: DecodeVisualEvent) => {
    if (destroyed) return;
    options.onVisualEvent?.(event);
    parent.dispatchEvent(
      new CustomEvent<DecodeVisualEvent>('aztec-realms:decode-visual', { detail: event }),
    );
  };

  class DecodeScene extends Phaser.Scene {
    private board?: InstanceType<typeof Phaser.GameObjects.Graphics>;
    private channelOverlay?: InstanceType<typeof Phaser.GameObjects.Graphics>;
    private centralEnergy = initialState.solved ? 1 : 0.18;
    private energyLevels: Record<string, number> = {};
    private hasTabletArt = false;
    private liftLevels: Record<string, number> = {};
    private segmentPlates = new Map<string, InstanceType<typeof Phaser.GameObjects.Image>>();
    private materialFaces = new Map<
      string,
      { image: InstanceType<typeof Phaser.GameObjects.Image>; x: number; y: number; key: string }
    >();
    private centralRelief?: InstanceType<typeof Phaser.GameObjects.Image>;
    private visualRotations: Record<string, number> = { ...initialState.rotations };
    private visualState: DecodeVisualState = deriveDecodeVisualState(
      puzzle,
      initialState.rotations,
    );

    constructor() {
      super('decode');
      for (const segment of puzzle.segments) {
        this.visualRotations[segment.id] = initialState.rotations[segment.id] ?? segment.rotation;
        this.energyLevels[segment.id] = this.visualState.segments[segment.id]?.energized ? 1 : 0;
      }
    }

    preload() {
      const environment = environmentAssetManifest.decode;
      const tablet = generatedRuntimeAssetManifest.decodeTabletFoundation;
      const turningStones = [
        generatedRuntimeAssetManifest.decodeLimestoneSurface,
        generatedRuntimeAssetManifest.decodeObsidianSurface,
        generatedRuntimeAssetManifest.decodeSolarRelief,
        generatedRuntimeAssetManifest.decodeGlyphReliefs,
      ];
      if (!this.textures.exists(environment.id)) {
        this.load.image(environment.id, environment.url);
      }
      if (!this.textures.exists(tablet.id)) {
        this.load.image(tablet.id, tablet.url);
      }
      {
        for (const turningStone of turningStones) {
          if (!this.textures.exists(turningStone.id)) {
            this.load.image(turningStone.id, turningStone.url);
          }
        }
      }
    }

    create() {
      if (destroyed) return;
      const requiredArt = [
        environmentAssetManifest.decode,
        generatedRuntimeAssetManifest.decodeTabletFoundation,
        generatedRuntimeAssetManifest.decodeLimestoneSurface,
        generatedRuntimeAssetManifest.decodeObsidianSurface,
        generatedRuntimeAssetManifest.decodeSolarRelief,
        generatedRuntimeAssetManifest.decodeGlyphReliefs,
      ];
      if (requiredArt.some((asset) => !this.textures.exists(asset.id))) {
        options.onUnavailable?.();
        return;
      }
      let revealed = false;
      const reportAfterRender = () => {
        if (revealed || destroyed || !this.layout().renderable) return;
        this.game.events.once('postrender', () => {
          if (revealed || destroyed || !this.layout().renderable) return;
          revealed = true;
          options.onReady();
        });
      };
      this.rebuildScene();
      this.scale.on('resize', () => {
        this.rebuildScene();
        reportAfterRender();
      });
      this.input.on('pointerdown', (pointer: InstanceType<typeof Phaser.Input.Pointer>) => {
        if (state.solved) return;
        const segment = this.segmentAt(pointer.x, pointer.y);
        if (!segment || segment.locked) return;
        this.pressSegment(segment);
        options.onSegmentSelected(segment.id);
      });
      reportAfterRender();
    }

    redraw(nextState: DecodeRendererState) {
      if (destroyed) return;
      const previousState = state;
      const previousVisualState = this.visualState;
      state = nextState;
      this.visualState = deriveDecodeVisualState(puzzle, nextState.rotations);

      let rotationAnimated = false;
      for (const segment of puzzle.segments) {
        const previous = previousState.rotations[segment.id] ?? segment.rotation;
        const next = nextState.rotations[segment.id] ?? segment.rotation;
        if (normalizeRotation(previous) === normalizeRotation(next)) {
          this.visualRotations[segment.id] = next;
          continue;
        }
        rotationAnimated = true;
        this.animateRotation(segment, previous, next, previousVisualState);
      }

      if (!previousState.solved && nextState.solved) {
        this.animateSolvedCascade();
      } else {
        this.animateEnergyChange(previousVisualState);
      }
      if (!rotationAnimated) this.drawBoard();
    }

    private layout(): BoardLayout {
      return decodeBoardLayout(this.scale.width, this.scale.height, puzzle.ringCount);
    }

    private rebuildScene() {
      if (destroyed) return;
      this.children.removeAll(true);
      this.board = undefined;
      this.channelOverlay = undefined;
      this.segmentPlates.clear();
      for (const face of this.materialFaces.values()) this.textures.remove(face.key);
      this.materialFaces.clear();
      this.centralRelief = undefined;
      const layout = this.layout();
      if (!layout.renderable) return;
      const environment = environmentAssetManifest.decode;
      const tablet = generatedRuntimeAssetManifest.decodeTabletFoundation;
      const hasEnvironmentArt = this.textures.exists(environment.id);
      this.hasTabletArt = this.textures.exists(tablet.id);
      if (hasEnvironmentArt) {
        const art = this.add.image(layout.centerX, layout.centerY, environment.id);
        art.setScale(Math.max(layout.width / art.width, layout.height / art.height));
        art.setAlpha(0.96);
      }

      const atmosphere = this.add.graphics();
      atmosphere.fillGradientStyle(
        palette.deep,
        palette.panel,
        material.obsidianDeep,
        palette.elevated,
        hasEnvironmentArt ? 0.08 : 1,
        hasEnvironmentArt ? 0.16 : 1,
        hasEnvironmentArt ? 0.5 : 1,
        hasEnvironmentArt ? 0.5 : 1,
      );
      atmosphere.fillRect(0, 0, layout.width, layout.height);
      atmosphere.fillStyle(material.obsidianDeep, hasEnvironmentArt ? 0.62 : 0.94);
      atmosphere.fillCircle(layout.centerX, layout.centerY, layout.boardRadius * 1.06);
      atmosphere.fillStyle(palette.gold, 0.05);
      atmosphere.fillCircle(layout.centerX, layout.centerY, layout.boardRadius * 1.02);
      if (this.hasTabletArt) {
        const tabletArt = this.add.image(layout.centerX, layout.centerY, tablet.id);
        tabletArt.setDisplaySize(layout.boardRadius * 2.2, layout.boardRadius * 2.2);
        tabletArt.setAlpha(0.96);
      }
      this.board = this.add.graphics();
      const rimMaterial = generatedRuntimeAssetManifest.decodeLimestoneSurface;
      if (this.textures.exists(rimMaterial.id)) {
        const source = this.textures.get(rimMaterial.id).getSourceImage() as HTMLImageElement;
        for (let block = 0; block < 18; block += 1) {
          const start = (block / 18) * Math.PI * 2;
          const points = annularPoints(
            layout.centerX,
            layout.centerY,
            layout.boardRadius * 1.008,
            layout.boardRadius * 1.065,
            start + 0.006,
            start + (Math.PI * 2) / 18 - 0.006,
          );
          const baked = bakeDecodeMaterial(points, source, block * 371, false);
          const key = `decode-rim-${block}`;
          this.textures.addCanvas(key, baked.canvas);
          const image = this.add.image(baked.bounds.left, baked.bounds.top, key).setOrigin(0);
          image.setDisplaySize(baked.bounds.width, baked.bounds.height);
          this.materialFaces.set(key, { image, x: baked.bounds.left, y: baked.bounds.top, key });
        }
      }
      const glyphAtlas = generatedRuntimeAssetManifest.decodeGlyphReliefs;
      if (this.textures.exists(glyphAtlas.id)) {
        const texture = this.textures.get(glyphAtlas.id);
        const source = texture.getSourceImage() as HTMLImageElement;
        for (let frame = 0; frame < 4; frame += 1) {
          const width = Math.floor(source.width / 2);
          const height = Math.floor(source.height / 2);
          if (!texture.has(`glyph-${frame}`)) {
            texture.add(
              `glyph-${frame}`,
              0,
              (frame % 2) * width,
              Math.floor(frame / 2) * height,
              width,
              height,
            );
          }
        }
      }
      {
        for (const segment of puzzle.segments) {
          const turningStone = this.isObsidianSurface(segment)
            ? generatedRuntimeAssetManifest.decodeObsidianSurface
            : generatedRuntimeAssetManifest.decodeLimestoneSurface;
          if (!this.textures.exists(turningStone.id)) continue;
          const geometry = this.segmentGeometry(segment);
          const points = annularPoints(
            geometry.centerX,
            geometry.centerY,
            geometry.innerRadius,
            geometry.outerRadius,
            geometry.start,
            geometry.end,
          );
          const baked = bakeDecodeMaterial(
            points,
            this.textures.get(turningStone.id).getSourceImage() as HTMLImageElement,
            stableHash(`${puzzle.seed}:${segment.id}`),
            this.isObsidianSurface(segment),
          );
          const key = `decode-face-${segment.id}`;
          this.textures.addCanvas(key, baked.canvas);
          const face = this.add.image(baked.bounds.left, baked.bounds.top, key).setOrigin(0);
          face.setDisplaySize(baked.bounds.width, baked.bounds.height);
          this.materialFaces.set(segment.id, {
            image: face,
            x: baked.bounds.left,
            y: baked.bounds.top,
            key,
          });
          if (this.textures.exists(glyphAtlas.id)) {
            const plate = this.add.image(
              0,
              0,
              glyphAtlas.id,
              `glyph-${stableHash(segment.id) % 4}`,
            );
            this.segmentPlates.set(segment.id, plate);
          }
        }
      }
      const core = generatedRuntimeAssetManifest.decodeSolarRelief;
      if (this.textures.exists(core.id)) {
        this.centralRelief = this.add.image(layout.centerX, layout.centerY, core.id);
      }
      this.channelOverlay = this.add.graphics();
      this.drawBoard();
    }

    private segmentGeometry(segment: DecodeSegmentView, lift = 0) {
      const layout = this.layout();
      const slots = Math.max(1, puzzle.sectorsPerRing[segment.ringIndex] ?? 1);
      const sector = (Math.PI * 2) / slots;
      const gap = Math.min(0.034, sector * 0.075);
      const seamStartAngle = -Math.PI / 2 + segment.angularSlot * sector;
      const seamEndAngle = seamStartAngle + sector;
      const start = seamStartAngle + gap;
      const end = start + sector - gap * 2;
      const innerSeamRadius = layout.centerRadius + segment.ringIndex * layout.ringWidth;
      const outerSeamRadius = layout.centerRadius + (segment.ringIndex + 1) * layout.ringWidth;
      const innerRadius = innerSeamRadius + layout.ringWidth * 0.045 + lift;
      const outerRadius = outerSeamRadius - layout.ringWidth * 0.045 + lift;
      return {
        ...layout,
        end,
        innerRadius,
        innerSeamRadius,
        middleAngle: (start + end) / 2,
        middleRadius: (innerRadius + outerRadius) / 2,
        outerRadius,
        outerSeamRadius,
        seamEndAngle,
        seamStartAngle,
        start,
      };
    }

    private segmentAt(x: number, y: number): DecodeSegmentView | undefined {
      const layout = this.layout();
      if (destroyed || !layout.renderable) return undefined;
      const distance = Phaser.Math.Distance.Between(x, y, layout.centerX, layout.centerY);
      if (distance < layout.centerRadius || distance > layout.boardRadius) return undefined;
      const ringIndex = Math.min(
        puzzle.ringCount - 1,
        Math.floor((distance - layout.centerRadius) / layout.ringWidth),
      );
      const slots = Math.max(1, puzzle.sectorsPerRing[ringIndex] ?? 1);
      const rawAngle = Math.atan2(y - layout.centerY, x - layout.centerX) + Math.PI / 2;
      const normalized = (rawAngle + Math.PI * 2) % (Math.PI * 2);
      const angularSlot = Math.floor(normalized / ((Math.PI * 2) / slots));
      return puzzle.segments.find(
        (segment) => segment.ringIndex === ringIndex && segment.angularSlot === angularSlot,
      );
    }

    private drawBoard() {
      if (destroyed) return;
      const graphics = this.board;
      const overlay = this.channelOverlay;
      if (!graphics || !overlay) return;
      graphics.clear();
      overlay.clear();
      const layout = this.layout();
      // Phaser can resize a detached host to zero before its deferred destruction.
      // Do not enqueue ring arcs (including radius - 1.5) for that transient layout.
      if (!layout.renderable) return;

      graphics.fillStyle(material.obsidianDeep, this.hasTabletArt ? 0.08 : 0.98);
      graphics.fillCircle(layout.centerX + 2, layout.centerY + 6, layout.boardRadius * 1.045);
      if (!this.materialFaces.has('decode-rim-0')) {
        graphics.lineStyle(Math.max(9, layout.boardRadius * 0.045), material.stoneDark, 1);
        graphics.strokeCircle(layout.centerX, layout.centerY, layout.boardRadius * 1.045);
        graphics.lineStyle(Math.max(4, layout.boardRadius * 0.018), material.stoneLight, 0.84);
        graphics.strokeCircle(layout.centerX, layout.centerY, layout.boardRadius * 1.015);
        graphics.lineStyle(2, palette.gold, 0.74);
        graphics.strokeCircle(layout.centerX, layout.centerY, layout.boardRadius * 1.075);
        graphics.lineStyle(1, palette.jade, 0.72);
        graphics.strokeCircle(layout.centerX, layout.centerY, layout.boardRadius * 1.095);
      }

      for (let ring = 0; ring <= puzzle.ringCount; ring += 1) {
        const radius = layout.centerRadius + ring * layout.ringWidth;
        graphics.lineStyle(Math.max(3, layout.ringWidth * 0.075), material.obsidianDeep, 0.96);
        graphics.strokeCircle(layout.centerX, layout.centerY, radius);
        graphics.lineStyle(1.2, material.stoneLight, 0.34);
        graphics.strokeCircle(layout.centerX, layout.centerY, radius - 1.5);
        graphics.lineStyle(1, palette.gold, 0.24);
        graphics.strokeCircle(layout.centerX, layout.centerY, radius + 1.5);
      }

      for (const segment of puzzle.segments) this.drawSegment(graphics, overlay, segment);
      this.drawConnectionSeams(overlay, layout);
      this.drawSourceBridges(overlay, layout);
      this.drawCentralGlyph(overlay, layout);
      if (state.solved) this.drawSolvedResidue(overlay, layout);
    }

    private isObsidianSurface(segment: DecodeSegmentView): boolean {
      return segment.locked || (segment.ringIndex * 3 + segment.angularSlot) % 5 === 0;
    }

    private drawSegment(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      overlay: InstanceType<typeof Phaser.GameObjects.Graphics>,
      segment: DecodeSegmentView,
    ) {
      const visual = this.visualState.segments[segment.id];
      const selected = state.selectedSegmentId === segment.id;
      const suggested = state.suggestedSegmentId === segment.id;
      const lift = (this.liftLevels[segment.id] ?? 0) + (selected ? 1.5 : 0);
      const geometry = this.segmentGeometry(segment, lift);
      const points = annularPoints(
        geometry.centerX,
        geometry.centerY,
        geometry.innerRadius,
        geometry.outerRadius,
        geometry.start,
        geometry.end,
      );
      const shadowPoints = points.map((point) => ({ x: point.x + 1.5, y: point.y + 4 }));
      const obsidianSurface = this.isObsidianSurface(segment);

      graphics.fillStyle(material.obsidianDeep, 0.9);
      graphics.fillPoints(shadowPoints, true);
      // Canvas rendering does not preserve Phaser's four-corner gradient on
      // arbitrary polygons consistently. Establish the material with an
      // explicit fill, then build the bevel from directional edge light.
      graphics.fillStyle(
        obsidianSurface ? material.obsidianSheen : material.stoneMid,
        this.hasTabletArt ? (obsidianSurface ? 0.78 : 0.58) : 1,
      );
      graphics.fillPoints(points, true);
      const facePoints = points.map((point) => ({
        x: point.x + (geometry.centerX - point.x) * 0.012,
        y: point.y + (geometry.centerY - point.y) * 0.012 - 0.8,
      }));
      graphics.fillStyle(
        obsidianSurface ? shade(material.obsidianSheen, 12) : material.stoneLight,
        obsidianSurface ? 0.12 : 0.16,
      );
      graphics.fillPoints(facePoints, true);
      graphics.lineStyle(selected ? 3.2 : 1.4, selected ? palette.solar : material.obsidianDeep, 1);
      graphics.strokePoints(points, true);
      graphics.lineStyle(1, obsidianSurface ? palette.jade : material.stoneLight, 0.66);
      graphics.strokePoints(points, true);

      const outerArc = points.slice(0, 15);
      const innerArc = points.slice(15);
      graphics.lineStyle(
        Math.max(1.2, geometry.ringWidth * 0.035),
        obsidianSurface ? palette.jade : material.stoneLight,
        obsidianSurface ? 0.34 : 0.82,
      );
      graphics.strokePoints(outerArc, false);
      graphics.lineStyle(
        Math.max(2, geometry.ringWidth * 0.05),
        material.obsidianDeep,
        obsidianSurface ? 0.88 : 0.5,
      );
      graphics.strokePoints(innerArc, false);

      const firstOuter = outerArc[0];
      const lastOuter = outerArc.at(-1);
      const firstInner = innerArc.at(-1);
      const lastInner = innerArc[0];
      if (firstOuter && firstInner && lastOuter && lastInner) {
        graphics.lineStyle(2, material.stoneLight, obsidianSurface ? 0.2 : 0.58);
        graphics.lineBetween(firstOuter.x, firstOuter.y, firstInner.x, firstInner.y);
        graphics.lineStyle(2.6, material.obsidianDeep, 0.72);
        graphics.lineBetween(lastOuter.x, lastOuter.y, lastInner.x, lastInner.y);
      }

      if (!this.materialFaces.has(segment.id)) {
        this.drawSegmentRelief(graphics, segment, geometry, obsidianSurface);
        this.drawWeathering(graphics, segment, geometry);
      }
      const face = this.materialFaces.get(segment.id);
      if (face) {
        face.image.setPosition(
          face.x + Math.cos(geometry.middleAngle) * lift,
          face.y + Math.sin(geometry.middleAngle) * lift - lift * 0.4,
        );
        face.image.setAlpha(1);
      }
      this.updateTurningStone(segment, geometry, obsidianSurface, selected);
      this.drawChannel(overlay, segment, geometry);

      if (segment.locked) this.drawLock(overlay, geometry);
      if (selected || suggested) this.drawSelection(overlay, geometry, suggested);
      if (visual?.terminal) this.drawTerminal(overlay, geometry, visual.terminalReached);
    }

    private updateTurningStone(
      segment: DecodeSegmentView,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
      obsidianSurface: boolean,
      selected: boolean,
    ) {
      const plate = this.segmentPlates.get(segment.id);
      if (!plate) return;
      const visualRotation = this.visualRotations[segment.id] ?? segment.rotation;
      const reliefAngle = geometry.middleAngle + (geometry.end - geometry.start) * 0.26;
      const x = geometry.centerX + Math.cos(reliefAngle) * geometry.middleRadius;
      const y = geometry.centerY + Math.sin(reliefAngle) * geometry.middleRadius;
      // The carving makes the quarter-turn legible; the entire stone remains interactive.
      const diameter = Math.max(18, geometry.ringWidth * (obsidianSurface ? 0.8 : 0.76));
      plate.setPosition(x, y);
      plate.setScale(diameter / Math.max(plate.width, plate.height));
      plate.setRotation((visualRotation * Math.PI) / 2);
      plate.setAlpha(selected ? 0.95 : segment.locked ? 0.62 : 0.82);
    }

    private drawWeathering(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      segment: DecodeSegmentView,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
    ) {
      const seed = stableHash(`${puzzle.seed}:${segment.id}`);
      const marks = options.qualityTier === 'low' ? 2 : 5;
      for (let mark = 0; mark < marks; mark += 1) {
        const angleProgress = 0.2 + (((seed >> (mark * 3)) & 15) / 15) * 0.6;
        const radiusProgress = 0.18 + (((seed >> (mark * 4 + 2)) & 15) / 15) * 0.64;
        const angle = geometry.start + (geometry.end - geometry.start) * angleProgress;
        const radius =
          geometry.innerRadius + (geometry.outerRadius - geometry.innerRadius) * radiusProgress;
        const x = geometry.centerX + Math.cos(angle) * radius;
        const y = geometry.centerY + Math.sin(angle) * radius;
        graphics.fillStyle(
          segment.locked ? palette.jade : material.stoneDark,
          segment.locked ? 0.18 : 0.3,
        );
        graphics.fillCircle(x, y, mark % 2 === 0 ? 1.4 : 0.85);
      }

      if (!segment.locked) {
        const glyphRadius = geometry.middleRadius + (stableHash(segment.id) % 2 === 0 ? 7 : -7);
        const glyphAngle = geometry.middleAngle + ((stableHash(segment.id) % 3) - 1) * 0.08;
        const x = geometry.centerX + Math.cos(glyphAngle) * glyphRadius;
        const y = geometry.centerY + Math.sin(glyphAngle) * glyphRadius;
        const tangentX = -Math.sin(glyphAngle);
        const tangentY = Math.cos(glyphAngle);
        graphics.lineStyle(1.4, material.stoneDark, 0.58);
        graphics.lineBetween(
          x - tangentX * 5,
          y - tangentY * 5,
          x + tangentX * 5,
          y + tangentY * 5,
        );
        graphics.strokeCircle(x, y, 2.8);
      }
    }

    private drawSegmentRelief(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      segment: DecodeSegmentView,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
      obsidianSurface: boolean,
    ) {
      const span = geometry.end - geometry.start;
      const insetStart = geometry.start + span * 0.045;
      const insetEnd = geometry.end - span * 0.045;
      const insetInner = geometry.innerRadius + geometry.ringWidth * 0.105;
      const insetOuter = geometry.outerRadius - geometry.ringWidth * 0.105;
      const insetPoints = annularPoints(
        geometry.centerX,
        geometry.centerY,
        insetInner,
        insetOuter,
        insetStart,
        insetEnd,
      );

      // Each wedge reads as a separately seated slab: dark undercut on its lower/right edge,
      // worn light on the raised edge and a faint material variation over the common tablet.
      graphics.fillStyle(
        obsidianSurface ? material.obsidianDeep : material.stoneShade,
        obsidianSurface ? 0.2 : 0.1,
      );
      graphics.fillPoints(
        insetPoints.map((point) => ({ x: point.x + 1.4, y: point.y + 2.2 })),
        true,
      );
      graphics.fillStyle(
        obsidianSurface ? palette.jade : material.stoneLight,
        obsidianSurface ? 0.065 : 0.09,
      );
      graphics.fillPoints(insetPoints, true);
      graphics.lineStyle(
        Math.max(2, geometry.ringWidth * 0.045),
        material.obsidianDeep,
        obsidianSurface ? 0.82 : 0.44,
      );
      graphics.strokePoints(insetPoints, true);
      graphics.lineStyle(
        Math.max(0.9, geometry.ringWidth * 0.014),
        obsidianSurface ? palette.jade : material.stoneLight,
        obsidianSurface ? 0.3 : 0.68,
      );
      graphics.strokePoints(
        insetPoints.map((point) => ({ x: point.x - 0.7, y: point.y - 0.9 })),
        true,
      );

      const seed = stableHash(`${puzzle.seed}:relief:${segment.id}`);
      const bandRadius = geometry.outerRadius - geometry.ringWidth * 0.16;
      const bandInset = span * (0.13 + ((seed >> 3) & 3) * 0.018);
      graphics.lineStyle(
        Math.max(4, geometry.ringWidth * 0.072),
        material.obsidianDeep,
        obsidianSurface ? 0.72 : 0.32,
      );
      graphics.beginPath();
      graphics.arc(
        geometry.centerX,
        geometry.centerY + 1.2,
        bandRadius,
        geometry.start + bandInset,
        geometry.end - bandInset,
      );
      graphics.strokePath();
      graphics.lineStyle(
        Math.max(1, geometry.ringWidth * 0.018),
        obsidianSurface ? palette.gold : palette.jade,
        obsidianSurface ? 0.42 : 0.3,
      );
      graphics.beginPath();
      graphics.arc(
        geometry.centerX,
        geometry.centerY,
        bandRadius - 0.8,
        geometry.start + bandInset,
        geometry.end - bandInset,
      );
      graphics.strokePath();

      if (options.qualityTier === 'low') return;

      const direction = (seed & 1) === 0 ? -1 : 1;
      const motifAngle = geometry.middleAngle + direction * span * 0.27;
      const motifRadius =
        geometry.middleRadius + ((((seed >> 5) & 3) - 1.5) / 1.5) * geometry.ringWidth * 0.13;
      const radial = { x: Math.cos(motifAngle), y: Math.sin(motifAngle) };
      const tangent = { x: -radial.y, y: radial.x };
      const motifCenter = {
        x: geometry.centerX + radial.x * motifRadius,
        y: geometry.centerY + radial.y * motifRadius,
      };
      const motifSize = Math.max(3.2, geometry.ringWidth * 0.072);
      const localPoint = (radialOffset: number, tangentOffset: number): Point => ({
        x: motifCenter.x + radial.x * radialOffset + tangent.x * tangentOffset,
        y: motifCenter.y + radial.y * radialOffset + tangent.y * tangentOffset,
      });
      const reliefMotif = [
        localPoint(-motifSize, -motifSize),
        localPoint(-motifSize * 0.25, -motifSize),
        localPoint(-motifSize * 0.25, -motifSize * 0.28),
        localPoint(motifSize * 0.34, -motifSize * 0.28),
        localPoint(motifSize * 0.34, motifSize * 0.42),
        localPoint(motifSize, motifSize * 0.42),
      ];
      graphics.lineStyle(Math.max(2.2, geometry.ringWidth * 0.034), material.obsidianDeep, 0.44);
      graphics.strokePoints(
        reliefMotif.map((point) => ({ x: point.x + 1, y: point.y + 1.2 })),
        false,
      );
      graphics.lineStyle(
        Math.max(0.9, geometry.ringWidth * 0.014),
        obsidianSurface ? material.stoneLight : material.stoneDark,
        obsidianSurface ? 0.46 : 0.38,
      );
      graphics.strokePoints(reliefMotif, false);

      const tesseraCount = span > 1.1 ? 3 : 2;
      for (let index = 0; index < tesseraCount; index += 1) {
        const progress = index / (tesseraCount - 1);
        const angle =
          geometry.start + bandInset * 0.75 + (span - bandInset * 1.5) * (0.34 + progress * 0.32);
        const x = geometry.centerX + Math.cos(angle) * bandRadius;
        const y = geometry.centerY + Math.sin(angle) * bandRadius;
        const radius = Math.max(1.7, geometry.ringWidth * 0.028);
        graphics.fillStyle(material.obsidianDeep, 0.92);
        graphics.fillCircle(x + 0.7, y + 1, radius * 1.42);
        graphics.fillStyle(
          index === 1 && segment.channelShape === 'fork' ? palette.gold : palette.jade,
          0.72,
        );
        graphics.fillCircle(x, y, radius);
        graphics.lineStyle(0.8, material.stoneLight, 0.42);
        graphics.strokeCircle(x - 0.4, y - 0.4, radius * 0.72);
      }

      const crackAngle = geometry.start + span * (0.22 + ((seed >> 8) & 15) / 28);
      const crackRadius =
        geometry.innerRadius + geometry.ringWidth * (0.2 + ((seed >> 12) & 7) / 18);
      const crackRadial = { x: Math.cos(crackAngle), y: Math.sin(crackAngle) };
      const crackTangent = { x: -crackRadial.y, y: crackRadial.x };
      const crackStart = {
        x: geometry.centerX + crackRadial.x * crackRadius,
        y: geometry.centerY + crackRadial.y * crackRadius,
      };
      graphics.lineStyle(Math.max(0.8, geometry.ringWidth * 0.012), material.obsidianDeep, 0.34);
      graphics.beginPath();
      graphics.moveTo(crackStart.x, crackStart.y);
      graphics.lineTo(
        crackStart.x + crackRadial.x * motifSize * 0.7 + crackTangent.x * motifSize * 0.3,
        crackStart.y + crackRadial.y * motifSize * 0.7 + crackTangent.y * motifSize * 0.3,
      );
      graphics.lineTo(
        crackStart.x + crackRadial.x * motifSize * 1.55 - crackTangent.x * motifSize * 0.16,
        crackStart.y + crackRadial.y * motifSize * 1.55 - crackTangent.y * motifSize * 0.16,
      );
      graphics.strokePath();
    }

    private portPoint(
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
      portIndex: number,
    ): Point {
      const points: readonly Point[] = [
        {
          x: geometry.centerX + Math.cos(geometry.middleAngle) * geometry.innerSeamRadius,
          y: geometry.centerY + Math.sin(geometry.middleAngle) * geometry.innerSeamRadius,
        },
        {
          x: geometry.centerX + Math.cos(geometry.seamEndAngle) * geometry.middleRadius,
          y: geometry.centerY + Math.sin(geometry.seamEndAngle) * geometry.middleRadius,
        },
        {
          x: geometry.centerX + Math.cos(geometry.middleAngle) * geometry.outerSeamRadius,
          y: geometry.centerY + Math.sin(geometry.middleAngle) * geometry.outerSeamRadius,
        },
        {
          x: geometry.centerX + Math.cos(geometry.seamStartAngle) * geometry.middleRadius,
          y: geometry.centerY + Math.sin(geometry.seamStartAngle) * geometry.middleRadius,
        },
      ];
      const normalized = normalizeRotation(portIndex);
      const lower = Math.floor(normalized);
      const progress = normalized - lower;
      return pointBetween(
        points[lower] ?? points[0],
        points[(lower + 1) % 4] ?? points[0],
        progress,
      );
    }

    private anchoredPortPoint(
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
      port: DecodePort,
      angle: number,
    ): Point {
      const radius =
        port === 'inner'
          ? geometry.innerSeamRadius
          : port === 'outer'
            ? geometry.outerSeamRadius
            : geometry.middleRadius;
      return {
        x: geometry.centerX + Math.cos(angle) * radius,
        y: geometry.centerY + Math.sin(angle) * radius,
      };
    }

    private drawChannel(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      segment: DecodeSegmentView,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
    ) {
      const visualRotation = this.visualRotations[segment.id] ?? segment.rotation;
      const basePortNames =
        segment.channelShape === 'straight'
          ? (['inner', 'outer'] as const)
          : segment.channelShape === 'corner'
            ? (['inner', 'clockwise'] as const)
            : (['inner', 'clockwise', 'outer'] as const);
      const portIndex = {
        inner: 0,
        clockwise: 1,
        outer: 2,
        counter_clockwise: 3,
      } as const;
      const hub = {
        x: geometry.centerX + Math.cos(geometry.middleAngle) * geometry.middleRadius,
        y: geometry.centerY + Math.sin(geometry.middleAngle) * geometry.middleRadius,
      };
      const rotationSettled = Math.abs(visualRotation - Math.round(visualRotation)) < 0.001;
      const endpoints = basePortNames.flatMap((port) => {
        const orientedPort = orientedDecodePort(port, visualRotation);
        const points = rotationSettled
          ? decodePortAnchorAngles(puzzle, this.visualState, segment, orientedPort).map((angle) =>
              this.anchoredPortPoint(geometry, orientedPort, angle),
            )
          : [this.portPoint(geometry, portIndex[port] + visualRotation)];
        return points.map((point) => ({
          channel: segment.channelTypes?.[port] ?? 'jade',
          point,
        }));
      });
      const visual = this.visualState.segments[segment.id];
      const energy = visibleDecodeEnergy(
        this.visualState,
        segment.id,
        this.energyLevels[segment.id],
      );

      if (energy > 0.01) {
        const glowColor = palette.solar;
        graphics.fillStyle(glowColor, 0.055 * energy);
        graphics.fillCircle(hub.x, hub.y, Math.max(10, geometry.ringWidth * 0.31));
        graphics.lineStyle(Math.max(2, geometry.ringWidth * 0.025), glowColor, 0.26 * energy);
        graphics.strokeCircle(hub.x, hub.y, Math.max(8, geometry.ringWidth * 0.245));
      }

      graphics.lineStyle(Math.max(9, geometry.ringWidth * 0.15), material.obsidianDeep, 0.98);
      for (const endpoint of endpoints) {
        graphics.lineBetween(hub.x + 1.2, hub.y + 2, endpoint.point.x + 1.2, endpoint.point.y + 2);
      }
      graphics.lineStyle(Math.max(7, geometry.ringWidth * 0.12), material.stoneLight, 0.72);
      for (const endpoint of endpoints) {
        graphics.lineBetween(
          hub.x - 0.7,
          hub.y - 0.7,
          endpoint.point.x - 0.7,
          endpoint.point.y - 0.7,
        );
      }
      graphics.lineStyle(Math.max(5.5, geometry.ringWidth * 0.09), material.obsidianDeep, 1);
      for (const endpoint of endpoints) {
        graphics.lineBetween(hub.x, hub.y, endpoint.point.x, endpoint.point.y);
      }
      graphics.fillStyle(material.obsidianDeep, 1);
      graphics.fillCircle(hub.x, hub.y, Math.max(4, geometry.ringWidth * 0.075));

      for (const endpoint of endpoints) {
        const baseInlay =
          segment.locked || endpoint.channel === 'gold' ? palette.gold : palette.jade;
        graphics.lineStyle(
          Math.max(2.8, geometry.ringWidth * 0.057),
          baseInlay,
          segment.locked ? 0.34 : 0.88,
        );
        graphics.lineBetween(hub.x, hub.y, endpoint.point.x, endpoint.point.y);
        graphics.lineStyle(Math.max(1, geometry.ringWidth * 0.014), shade(baseInlay, 38), 0.72);
        graphics.lineBetween(
          hub.x - 0.6,
          hub.y - 0.6,
          endpoint.point.x - 0.6,
          endpoint.point.y - 0.6,
        );
      }

      if (energy > 0.01) {
        graphics.lineStyle(Math.max(7, geometry.ringWidth * 0.11), palette.gold, 0.25 * energy);
        for (const endpoint of endpoints)
          graphics.lineBetween(hub.x, hub.y, endpoint.point.x, endpoint.point.y);
        graphics.lineStyle(Math.max(1.8, geometry.ringWidth * 0.035), palette.solar, 0.96 * energy);
        for (const endpoint of endpoints)
          graphics.lineBetween(hub.x, hub.y, endpoint.point.x, endpoint.point.y);
        graphics.fillStyle(palette.solar, 0.9 * energy);
        graphics.fillCircle(hub.x, hub.y, Math.max(2.3, geometry.ringWidth * 0.045));
      }

      if (visual?.interrupted && state.selectedSegmentId === segment.id) {
        graphics.lineStyle(2, palette.gold, 0.9);
        graphics.lineBetween(hub.x - 4, hub.y - 4, hub.x + 4, hub.y + 4);
        graphics.lineBetween(hub.x + 4, hub.y - 4, hub.x - 4, hub.y + 4);
      }
    }

    private drawConnectionSeams(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      layout: BoardLayout,
    ) {
      for (const connection of this.visualState.connections) {
        const left = puzzle.segments.find((segment) => segment.id === connection.leftSegmentId);
        const right = puzzle.segments.find((segment) => segment.id === connection.rightSegmentId);
        if (!left || !right) continue;
        const leftRotation = this.visualRotations[left.id] ?? left.rotation;
        const rightRotation = this.visualRotations[right.id] ?? right.rotation;
        const expectedLeft = state.rotations[left.id] ?? left.rotation;
        const expectedRight = state.rotations[right.id] ?? right.rotation;
        if (
          normalizeRotation(leftRotation) !== normalizeRotation(expectedLeft) ||
          normalizeRotation(rightRotation) !== normalizeRotation(expectedRight)
        )
          continue;

        const angle = decodePortAnchorAngles(
          puzzle,
          this.visualState,
          left,
          connection.leftPort,
        )[0];
        if (angle === undefined) continue;
        const radius =
          left.ringIndex === right.ringIndex
            ? layout.centerRadius + (left.ringIndex + 0.5) * layout.ringWidth
            : layout.centerRadius + Math.max(left.ringIndex, right.ringIndex) * layout.ringWidth;
        const x = layout.centerX + Math.cos(angle) * radius;
        const y = layout.centerY + Math.sin(angle) * radius;
        const energized =
          this.visualState.segments[left.id]?.energized &&
          this.visualState.segments[right.id]?.energized;
        graphics.fillStyle(material.obsidianDeep, 0.98);
        graphics.fillCircle(x, y, Math.max(3.2, layout.ringWidth * 0.052));
        graphics.fillStyle(energized ? palette.solar : palette.jade, energized ? 0.98 : 0.78);
        graphics.fillCircle(x, y, Math.max(1.5, layout.ringWidth * 0.022));
      }
    }

    private drawSourceBridges(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      layout: BoardLayout,
    ) {
      const configuredSources = new Set(puzzle.sourceSegmentIds ?? []);
      for (const segment of puzzle.segments) {
        if (
          segment.ringIndex !== 0 ||
          (configuredSources.size > 0 && !configuredSources.has(segment.id))
        )
          continue;
        const visualRotation = this.visualRotations[segment.id] ?? segment.rotation;
        const expectedRotation = state.rotations[segment.id] ?? segment.rotation;
        if (normalizeRotation(visualRotation) !== normalizeRotation(expectedRotation)) continue;
        if (!channelsForSegment(segment, visualRotation).has('inner')) continue;

        const angle = decodeSegmentCenterAngle(puzzle, segment);
        const startRadius = layout.centerRadius * 0.76;
        const endRadius = layout.centerRadius;
        const startX = layout.centerX + Math.cos(angle) * startRadius;
        const startY = layout.centerY + Math.sin(angle) * startRadius;
        const endX = layout.centerX + Math.cos(angle) * endRadius;
        const endY = layout.centerY + Math.sin(angle) * endRadius;
        graphics.lineStyle(Math.max(7, layout.ringWidth * 0.12), material.obsidianDeep, 0.98);
        graphics.lineBetween(startX, startY, endX, endY);
        graphics.lineStyle(Math.max(2.4, layout.ringWidth * 0.045), palette.solar, 0.96);
        graphics.lineBetween(startX, startY, endX, endY);
      }
    }

    private drawLock(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
    ) {
      const x = geometry.centerX + Math.cos(geometry.middleAngle) * geometry.middleRadius;
      const y = geometry.centerY + Math.sin(geometry.middleAngle) * geometry.middleRadius;
      const size = Math.max(5, geometry.ringWidth * 0.1);
      graphics.fillStyle(material.stoneDark, 0.95);
      graphics.fillRoundedRect(x - size, y - size * 0.15, size * 2, size * 1.35, 2);
      graphics.lineStyle(2, palette.gold, 0.92);
      graphics.strokeRoundedRect(x - size * 0.65, y - size, size * 1.3, size * 1.35, size * 0.55);
      graphics.strokeRoundedRect(x - size, y - size * 0.15, size * 2, size * 1.35, 2);
    }

    private drawSelection(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
      suggested: boolean,
    ) {
      const markerColor = suggested ? palette.turquoise : palette.solar;
      const hubX = geometry.centerX + Math.cos(geometry.middleAngle) * geometry.middleRadius;
      const hubY = geometry.centerY + Math.sin(geometry.middleAngle) * geometry.middleRadius;
      const plateRadius = Math.max(12, geometry.ringWidth * 0.34);
      const rim = annularPoints(
        geometry.centerX,
        geometry.centerY,
        geometry.innerRadius,
        geometry.outerRadius,
        geometry.start,
        geometry.end,
      );
      graphics.lineStyle(5, markerColor, 0.12);
      graphics.strokePoints(rim, true);
      graphics.lineStyle(1.8, markerColor, 0.98);
      graphics.strokePoints(rim, true);

      if (suggested) {
        const arrowRadius = plateRadius * 1.42;
        const arrowStart = -Math.PI * 0.72;
        const arrowEnd = Math.PI * 0.1;
        graphics.lineStyle(Math.max(3, geometry.ringWidth * 0.045), material.obsidianDeep, 0.94);
        graphics.beginPath();
        graphics.arc(hubX, hubY, arrowRadius, arrowStart, arrowEnd);
        graphics.strokePath();
        graphics.lineStyle(Math.max(1.6, geometry.ringWidth * 0.025), palette.solar, 1);
        graphics.beginPath();
        graphics.arc(hubX, hubY, arrowRadius, arrowStart, arrowEnd);
        graphics.strokePath();
        const tipX = hubX + Math.cos(arrowEnd) * arrowRadius;
        const tipY = hubY + Math.sin(arrowEnd) * arrowRadius;
        const tangentX = -Math.sin(arrowEnd);
        const tangentY = Math.cos(arrowEnd);
        const radialX = Math.cos(arrowEnd);
        const radialY = Math.sin(arrowEnd);
        const arrowWidth = Math.max(3.5, geometry.ringWidth * 0.06);
        graphics.fillStyle(palette.solar, 1);
        graphics.fillTriangle(
          tipX + tangentX * arrowWidth * 0.8,
          tipY + tangentY * arrowWidth * 0.8,
          tipX - tangentX * arrowWidth * 0.55 + radialX * arrowWidth,
          tipY - tangentY * arrowWidth * 0.55 + radialY * arrowWidth,
          tipX - tangentX * arrowWidth * 0.55 - radialX * arrowWidth,
          tipY - tangentY * arrowWidth * 0.55 - radialY * arrowWidth,
        );
      }
    }

    private drawTerminal(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      geometry: ReturnType<DecodeScene['segmentGeometry']>,
      energized: boolean,
    ) {
      const radius = geometry.outerSeamRadius;
      const x = geometry.centerX + Math.cos(geometry.middleAngle) * radius;
      const y = geometry.centerY + Math.sin(geometry.middleAngle) * radius;
      const size = Math.max(5, Math.min(9, geometry.ringWidth * 0.14));
      graphics.fillStyle(material.obsidianDeep, 1);
      graphics.fillCircle(x, y, size + 2);
      graphics.lineStyle(1.4, palette.gold, 1);
      graphics.strokeCircle(x, y, size + 2);
      graphics.fillStyle(energized ? palette.solar : palette.stone, 1);
      graphics.fillPoints(
        [
          { x, y: y - size },
          { x: x + size, y },
          { x, y: y + size },
          { x: x - size, y },
        ],
        true,
      );
    }

    private drawCentralGlyph(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      layout: BoardLayout,
    ) {
      const energy = state.solved ? this.centralEnergy : Math.max(0.18, this.centralEnergy * 0.45);
      const radius = layout.centerRadius * (0.88 + (state.solved ? this.centralEnergy * 0.025 : 0));
      if (this.centralRelief) {
        this.centralRelief.setPosition(
          layout.centerX,
          layout.centerY - (state.solved ? energy * 2 : 0),
        );
        this.centralRelief.setDisplaySize(radius * 1.92, radius * 1.92);
        graphics.lineStyle(4 + energy * 3, palette.gold, 0.12 * energy);
        graphics.strokeCircle(layout.centerX, layout.centerY, radius * 0.97);
        graphics.lineStyle(1.6, state.solved ? palette.solar : palette.jade, 0.6 + 0.35 * energy);
        graphics.strokeCircle(layout.centerX, layout.centerY, radius * 0.95);
        return;
      }
      graphics.fillStyle(palette.solar, 0.08 * energy);
      graphics.fillCircle(layout.centerX, layout.centerY, radius * 1.42);
      graphics.fillStyle(material.stoneDark, 1);
      graphics.fillCircle(layout.centerX + 1, layout.centerY + 3, radius * 1.06);
      graphics.fillGradientStyle(
        material.stoneLight,
        material.stoneMid,
        material.stoneShade,
        material.stoneDark,
        1,
      );
      graphics.fillCircle(layout.centerX, layout.centerY, radius);
      graphics.lineStyle(4, material.obsidianDeep, 1);
      graphics.strokeCircle(layout.centerX, layout.centerY, radius * 0.78);
      graphics.lineStyle(2.5, state.solved ? palette.solar : palette.jade, 0.92);
      graphics.strokeCircle(layout.centerX, layout.centerY, radius * 0.67);

      graphics.lineStyle(
        Math.max(2, radius * 0.065),
        state.solved ? palette.solar : material.obsidianDeep,
        0.96,
      );
      for (let ray = 0; ray < 8; ray += 1) {
        const angle = (Math.PI * 2 * ray) / 8;
        const inner = radius * 0.18;
        const outer = radius * (ray % 2 === 0 ? 0.5 : 0.42);
        graphics.lineBetween(
          layout.centerX + Math.cos(angle) * inner,
          layout.centerY + Math.sin(angle) * inner,
          layout.centerX + Math.cos(angle) * outer,
          layout.centerY + Math.sin(angle) * outer,
        );
      }
      graphics.fillStyle(state.solved ? palette.solar : palette.gold, 0.98);
      graphics.fillCircle(layout.centerX, layout.centerY, Math.max(3, radius * 0.1));
    }

    private drawSolvedResidue(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      layout: BoardLayout,
    ) {
      // Celebrate at the glyph, never draw fictitious connected rings across the puzzle.
      graphics.lineStyle(1.8, palette.solar, 0.24);
      graphics.strokeCircle(layout.centerX, layout.centerY, layout.centerRadius * 1.04);

      const moteCount = options.qualityTier === 'low' ? 8 : 18;
      for (let index = 0; index < moteCount; index += 1) {
        const seed = stableHash(`${puzzle.seed}:solved:${index}`);
        const angle = ((seed % 6283) / 1000) % (Math.PI * 2);
        const distance =
          layout.centerRadius * 1.25 +
          (((seed >>> 8) % 1000) / 1000) * (layout.boardRadius - layout.centerRadius * 1.55);
        const radius = 0.8 + ((seed >>> 18) % 4) * 0.42;
        graphics.fillStyle(
          index % 3 === 0 ? palette.turquoise : palette.solar,
          0.42 + (index % 4) * 0.1,
        );
        graphics.fillCircle(
          layout.centerX + Math.cos(angle) * distance,
          layout.centerY + Math.sin(angle) * distance,
          radius,
        );
      }
    }

    private pressSegment(segment: DecodeSegmentView) {
      const lift = { value: 0 };
      this.liftLevels[segment.id] = 0;
      this.tweens.add({
        targets: lift,
        value: options.reducedMotion ? 1 : 4,
        duration: options.reducedMotion ? 45 : 90,
        ease: 'Cubic.easeOut',
        yoyo: true,
        onUpdate: () => {
          this.liftLevels[segment.id] = lift.value;
          this.drawBoard();
        },
        onComplete: () => {
          this.liftLevels[segment.id] = 0;
          this.drawBoard();
        },
      });
      if (!options.reducedMotion) this.spawnDust(segment);
    }

    private animateRotation(
      segment: DecodeSegmentView,
      previous: number,
      next: number,
      previousVisualState: DecodeVisualState,
    ) {
      let delta = normalizeRotation(next - previous);
      if (delta > 2) delta -= 4;
      const motion = { rotation: previous };
      this.visualRotations[segment.id] = previous;
      this.tweens.add({
        targets: motion,
        rotation: previous + delta,
        duration: options.reducedMotion ? 90 : 190,
        ease: 'Cubic.easeOut',
        onUpdate: () => {
          this.visualRotations[segment.id] = motion.rotation;
          this.drawBoard();
        },
        onComplete: () => {
          this.visualRotations[segment.id] = next;
          this.drawBoard();
          this.snapPulse(segment);
          emit({ name: 'segment_rotate', segmentId: segment.id });
          const before = previousVisualState.segments[segment.id];
          const after = this.visualState.segments[segment.id];
          if (!before?.energized && after?.energized) {
            emit({ name: 'connection_made', segmentId: segment.id });
          } else if (after?.interrupted) {
            emit({ name: 'dead_end', segmentId: segment.id });
          }
        },
      });
    }

    private animateEnergyChange(previousVisualState: DecodeVisualState) {
      for (const segment of puzzle.segments) {
        const previous = previousVisualState.segments[segment.id]?.energized === true;
        const next = this.visualState.segments[segment.id]?.energized === true;
        if (previous === next) {
          this.energyLevels[segment.id] = next ? 1 : 0;
          continue;
        }
        if (!next || options.reducedMotion) {
          this.energyLevels[segment.id] = next ? 1 : 0;
          continue;
        }
        const energy = { value: 0 };
        this.energyLevels[segment.id] = 0;
        this.tweens.add({
          targets: energy,
          value: 1,
          delay: 110 + (this.visualState.segments[segment.id]?.depth ?? 0) * 45,
          duration: 210,
          ease: 'Sine.easeOut',
          onUpdate: () => {
            this.energyLevels[segment.id] = energy.value;
            this.drawBoard();
          },
        });
      }
      const energizedRatio =
        this.visualState.energizedSegmentIds.size / Math.max(1, puzzle.segments.length);
      this.centralEnergy = Math.max(0.18, energizedRatio);
    }

    private animateSolvedCascade() {
      const connectedIds = decodeCascadeSegments(this.visualState);
      const ordered = connectedIds.flatMap((id) =>
        puzzle.segments.filter((segment) => segment.id === id),
      );
      for (const segment of puzzle.segments) {
        if (!this.visualState.energizedSegmentIds.has(segment.id))
          this.energyLevels[segment.id] = 0;
      }
      if (options.reducedMotion) {
        for (const segment of ordered) this.energyLevels[segment.id] = 1;
        this.centralEnergy = 1;
        this.drawBoard();
        emit({ name: 'glyph_resonance' });
        emit({ name: 'puzzle_solved' });
        return;
      }

      this.centralEnergy = 0.2;
      for (const [index, segment] of ordered.entries()) {
        const energy = { value: this.energyLevels[segment.id] ?? 0 };
        this.tweens.add({
          targets: energy,
          value: 1,
          delay: index * 24,
          duration: 320,
          ease: 'Sine.easeInOut',
          onUpdate: () => {
            this.energyLevels[segment.id] = energy.value;
            this.drawBoard();
          },
        });
      }
      const center = { value: 0.2 };
      this.tweens.add({
        targets: center,
        value: 1,
        delay: Math.min(500, ordered.length * 24 + 120),
        duration: 440,
        ease: 'Cubic.easeOut',
        onStart: () => emit({ name: 'glyph_resonance' }),
        onUpdate: () => {
          this.centralEnergy = center.value;
          this.drawBoard();
        },
        onComplete: () => {
          this.spawnSolarMotes();
          emit({ name: 'puzzle_solved' });
        },
      });
    }

    private segmentCenter(segment: DecodeSegmentView): Point {
      const geometry = this.segmentGeometry(segment);
      return {
        x: geometry.centerX + Math.cos(geometry.middleAngle) * geometry.middleRadius,
        y: geometry.centerY + Math.sin(geometry.middleAngle) * geometry.middleRadius,
      };
    }

    private spawnDust(segment: DecodeSegmentView) {
      if (destroyed || !this.layout().renderable) return;
      const center = this.segmentCenter(segment);
      const seed = stableHash(segment.id);
      for (let index = 0; index < particleCount(options.qualityTier); index += 1) {
        const angle = ((seed + index * 97) % 628) / 100;
        const mote = this.add.circle(
          center.x + Math.cos(angle) * 5,
          center.y + Math.sin(angle) * 5,
          index % 2 === 0 ? 1.6 : 1,
          material.stoneLight,
          0.62,
        );
        this.tweens.add({
          targets: mote,
          x: mote.x + Math.cos(angle) * (10 + index * 2),
          y: mote.y + Math.sin(angle) * (10 + index * 2) + 5,
          alpha: 0,
          duration: 300 + index * 30,
          ease: 'Quad.easeOut',
          onComplete: () => mote.destroy(),
        });
      }
    }

    private snapPulse(segment: DecodeSegmentView) {
      if (destroyed || options.reducedMotion || !this.layout().renderable) return;
      const center = this.segmentCenter(segment);
      const ring = this.add.circle(center.x, center.y, 5, palette.solar, 0);
      ring.setStrokeStyle(2, palette.solar, 0.9);
      this.tweens.add({
        targets: ring,
        scale: 2.4,
        alpha: 0,
        duration: 180,
        ease: 'Cubic.easeOut',
        onComplete: () => ring.destroy(),
      });
    }

    private spawnSolarMotes() {
      const layout = this.layout();
      if (destroyed || !layout.renderable) return;
      const count = particleCount(options.qualityTier) + 2;
      for (let index = 0; index < count; index += 1) {
        const angle = (index / count) * Math.PI * 2;
        const mote = this.add.circle(layout.centerX, layout.centerY, 1.8, palette.solar, 0.92);
        this.tweens.add({
          targets: mote,
          x: layout.centerX + Math.cos(angle) * layout.centerRadius * 1.5,
          y: layout.centerY + Math.sin(angle) * layout.centerRadius * 1.5,
          alpha: 0,
          duration: 620,
          ease: 'Cubic.easeOut',
          onComplete: () => mote.destroy(),
        });
      }
    }
  }

  const scene = new DecodeScene();
  const game = new Phaser.Game({
    // Decode uses portable graphics, images and tweens; Canvas keeps route and
    // orientation changes deterministic without a transient WebGL framebuffer.
    type: Phaser.CANVAS,
    parent,
    width: Math.max(parent.clientWidth, 320),
    height: Math.max(parent.clientHeight, 390),
    backgroundColor: semanticColors.backgroundDeep,
    transparent: true,
    scene,
    render: { antialias: true, pixelArt: false, roundPixels: true },
    scale: { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.CENTER_BOTH },
    input: { activePointers: 2 },
  });

  return {
    update: (nextState) => scene.redraw(nextState),
    resize: () => {
      if (!destroyed)
        game.scale.resize(Math.max(parent.clientWidth, 320), Math.max(parent.clientHeight, 390));
    },
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      game.destroy(true);
    },
  };
}
